import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, Filter, X } from "lucide-react";
import { format } from "date-fns";

interface ActivityRecord {
  id: string;
  user_email: string;
  action: 'page_visit' | 'login' | 'download' | 'logout';
  page_path: string | null;
  duration: number | null;
  file_name: string | null;
  location: string | null;
  device_type: string | null;
  user_agent: string | null;
  created_at: string;
}

export const UserActivityTable = () => {
  const [excludeAdmins, setExcludeAdmins] = useState(true);
  const [actionFilter, setActionFilter] = useState<string>("all");
  const [userEmailFilter, setUserEmailFilter] = useState("");
  const [deviceFilter, setDeviceFilter] = useState<string>("all");
  const [locationFilter, setLocationFilter] = useState("");
  const [dateFrom, setDateFrom] = useState<Date | undefined>();
  const [dateTo, setDateTo] = useState<Date | undefined>();
  const [showFilters, setShowFilters] = useState(false);

  const { data: activities, isLoading } = useQuery({
    queryKey: ['user-activities', excludeAdmins, actionFilter, userEmailFilter, deviceFilter, locationFilter, dateFrom, dateTo],
    queryFn: async () => {
      let query = supabase
        .from('user_tracking')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(200);

      if (excludeAdmins) {
        query = query.neq('user_email', 'ahmed@supplyfied.com');
      }

      if (actionFilter && actionFilter !== "all") {
        query = query.eq('action', actionFilter as 'page_visit' | 'login' | 'download' | 'logout');
      }

      if (userEmailFilter) {
        query = query.ilike('user_email', `%${userEmailFilter}%`);
      }

      if (deviceFilter && deviceFilter !== "all") {
        query = query.eq('device_type', deviceFilter);
      }

      if (locationFilter) {
        query = query.ilike('location', `%${locationFilter}%`);
      }

      if (dateFrom) {
        query = query.gte('created_at', dateFrom.toISOString());
      }

      if (dateTo) {
        const endDate = new Date(dateTo);
        endDate.setHours(23, 59, 59, 999);
        query = query.lte('created_at', endDate.toISOString());
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as ActivityRecord[];
    }
  });

  if (isLoading) {
    return <div>Loading activity data...</div>;
  }

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return 'N/A';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const formatAction = (action: ActivityRecord['action']) => {
    const actionMap = {
      page_visit: 'Page Visit',
      login: 'Login',
      download: 'Download',
      logout: 'Logout'
    };
    return actionMap[action];
  };

  const clearAllFilters = () => {
    setExcludeAdmins(true);
    setActionFilter("all");
    setUserEmailFilter("");
    setDeviceFilter("all");
    setLocationFilter("");
    setDateFrom(undefined);
    setDateTo(undefined);
  };

  const hasActiveFilters = actionFilter !== "all" || userEmailFilter || deviceFilter !== "all" || locationFilter || dateFrom || dateTo || !excludeAdmins;

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <CardTitle>Recent User Activity</CardTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
                className="gap-2"
              >
                <Filter className="h-4 w-4" />
                Filters
              </Button>
              {hasActiveFilters && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearAllFilters}
                  className="gap-2"
                >
                  <X className="h-4 w-4" />
                  Clear
                </Button>
              )}
            </div>
          </div>
          
          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 p-4 bg-muted/50 rounded-lg">
              <div className="space-y-2">
                <label className="text-sm font-medium">Admin Filter</label>
                <Button
                  variant={excludeAdmins ? "default" : "outline"}
                  onClick={() => setExcludeAdmins(!excludeAdmins)}
                  size="sm"
                  className="w-full"
                >
                  {excludeAdmins ? "Exclude Admins" : "Include Admins"}
                </Button>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Action Type</label>
                <Select value={actionFilter} onValueChange={setActionFilter}>
                  <SelectTrigger className="bg-background">
                    <SelectValue placeholder="All actions" />
                  </SelectTrigger>
                  <SelectContent className="bg-background border z-50">
                    <SelectItem value="all">All actions</SelectItem>
                    <SelectItem value="page_visit">Page Visit</SelectItem>
                    <SelectItem value="login">Login</SelectItem>
                    <SelectItem value="download">Download</SelectItem>
                    <SelectItem value="logout">Logout</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Device Type</label>
                <Select value={deviceFilter} onValueChange={setDeviceFilter}>
                  <SelectTrigger className="bg-background">
                    <SelectValue placeholder="All devices" />
                  </SelectTrigger>
                  <SelectContent className="bg-background border z-50">
                    <SelectItem value="all">All devices</SelectItem>
                    <SelectItem value="desktop">Desktop</SelectItem>
                    <SelectItem value="mobile">Mobile</SelectItem>
                    <SelectItem value="tablet">Tablet</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">User Email</label>
                <Input
                  placeholder="Filter by email..."
                  value={userEmailFilter}
                  onChange={(e) => setUserEmailFilter(e.target.value)}
                  className="bg-background"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Location</label>
                <Input
                  placeholder="Filter by location..."
                  value={locationFilter}
                  onChange={(e) => setLocationFilter(e.target.value)}
                  className="bg-background"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Date From</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal bg-background"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {dateFrom ? format(dateFrom, "PPP") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-background border z-50" align="start">
                    <Calendar
                      mode="single"
                      selected={dateFrom}
                      onSelect={setDateFrom}
                      initialFocus
                      className="p-3 pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Date To</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal bg-background"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {dateTo ? format(dateTo, "PPP") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-background border z-50" align="start">
                    <Calendar
                      mode="single"
                      selected={dateTo}
                      onSelect={setDateTo}
                      initialFocus
                      className="p-3 pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Time</TableHead>
              <TableHead>User</TableHead>
              <TableHead>Action</TableHead>
              <TableHead>Details</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Device</TableHead>
              <TableHead>Duration</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {activities?.map((activity) => (
              <TableRow key={activity.id}>
                <TableCell>
                  {new Date(activity.created_at).toLocaleString()}
                </TableCell>
                <TableCell>{activity.user_email}</TableCell>
                <TableCell>{formatAction(activity.action)}</TableCell>
                <TableCell>
                  {activity.page_path || activity.file_name || 'N/A'}
                </TableCell>
                <TableCell>
                  {activity.location || 'N/A'}
                </TableCell>
                <TableCell>
                  {activity.device_type || 'N/A'}
                </TableCell>
                <TableCell>
                  {formatDuration(activity.duration)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};